// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * First-party dithering DSP. Everything here is pure and works on plain typed
 * arrays so it can run in a worker and be unit-tested without a DOM.
 *
 * Pipeline: brightness/contrast → build palette → dither (error diffusion or
 * ordered) → nearest-colour quantise to that palette.
 */

import type { AlgoId, DitherParams, PaletteId, RGB } from './types';

/** A decoded, working-resolution image. `data` is RGBA (length = w*h*4). */
export interface WorkImage {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

// ── Fixed palettes ──────────────────────────────────────────────────────

/** The original Game Boy (DMG) four-green ramp, dark → light. */
export const GAMEBOY: RGB[] = [
  [15, 56, 15],
  [48, 98, 48],
  [139, 172, 15],
  [155, 188, 15],
];

/** CGA mode 4, palette 1, high intensity: black, cyan, magenta, white. */
export const CGA: RGB[] = [
  [0, 0, 0],
  [85, 255, 255],
  [255, 85, 255],
  [255, 255, 255],
];

export const MONO: RGB[] = [
  [0, 0, 0],
  [255, 255, 255],
];

/** N evenly-spaced grey levels, dark → light (N ≥ 2). */
export function grayRamp(n: number): RGB[] {
  const levels = Math.max(2, Math.round(n));
  const out: RGB[] = [];
  for (let i = 0; i < levels; i++) {
    const v = Math.round((i / (levels - 1)) * 255);
    out.push([v, v, v]);
  }
  return out;
}

/** N-level sepia ramp, dark → light (N ≥ 2). */
export function sepiaRamp(n: number): RGB[] {
  const levels = Math.max(2, Math.round(n));
  const out: RGB[] = [];
  for (let i = 0; i < levels; i++) {
    const t = i / (levels - 1);
    out.push([
      Math.round(20 + t * 235),
      Math.round(13 + t * 222),
      Math.round(2 + t * 200),
    ]);
  }
  return out;
}

/** Resolve the parameter set (and optional image) into a concrete palette. */
export function buildPalette(params: DitherParams, img?: WorkImage): RGB[] {
  const p: PaletteId = params.palette;
  switch (p) {
    case 'mono':
      return MONO;
    case 'gameboy':
      return GAMEBOY;
    case 'cga':
      return CGA;
    case 'gray':
      return grayRamp(clampInt(params.colors, 2, 8));
    case 'sepia':
      return sepiaRamp(clampInt(params.colors, 2, 8));
    case 'adaptive':
      if (!img) return grayRamp(clampInt(params.colors, 2, 32));
      return medianCut(img.data, clampInt(params.colors, 2, 32));
    default:
      return MONO;
  }
}

// ── Median-cut adaptive palette ─────────────────────────────────────────

interface Box {
  pixels: RGB[];
}

/**
 * Median-cut colour quantisation. Samples the image (for speed), then
 * recursively splits the colour space along its longest axis until `n` boxes
 * exist, and returns the average colour of each box.
 */
export function medianCut(data: Uint8ClampedArray, n: number): RGB[] {
  const want = clampInt(n, 1, 256);
  const px = sampleOpaquePixels(data, 16384);
  if (px.length === 0) return grayRamp(want);
  if (px.length <= want) return dedupe(px).slice(0, want);

  let boxes: Box[] = [{ pixels: px }];
  while (boxes.length < want) {
    // Split the box with the largest single-channel range.
    let bestIdx = -1;
    let bestRange = -1;
    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].pixels.length < 2) continue;
      const r = boxRange(boxes[i]).range;
      if (r > bestRange) {
        bestRange = r;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) break; // nothing left to split
    const target = boxes[bestIdx];
    const { channel } = boxRange(target);
    target.pixels.sort((a, b) => a[channel] - b[channel]);
    const mid = target.pixels.length >> 1;
    const a: Box = { pixels: target.pixels.slice(0, mid) };
    const b: Box = { pixels: target.pixels.slice(mid) };
    boxes.splice(bestIdx, 1, a, b);
  }

  return boxes.map(averageColor);
}

function boxRange(box: Box): { channel: 0 | 1 | 2; range: number } {
  let rmin = 255,
    rmax = 0,
    gmin = 255,
    gmax = 0,
    bmin = 255,
    bmax = 0;
  for (const [r, g, b] of box.pixels) {
    if (r < rmin) rmin = r;
    if (r > rmax) rmax = r;
    if (g < gmin) gmin = g;
    if (g > gmax) gmax = g;
    if (b < bmin) bmin = b;
    if (b > bmax) bmax = b;
  }
  const rr = rmax - rmin,
    gr = gmax - gmin,
    br = bmax - bmin;
  // Weight green a touch higher — the eye is most sensitive to it.
  const rw = rr * 1.0,
    gw = gr * 1.2,
    bw = br * 0.8;
  if (gw >= rw && gw >= bw) return { channel: 1, range: gr };
  if (rw >= bw) return { channel: 0, range: rr };
  return { channel: 2, range: br };
}

function averageColor(box: Box): RGB {
  let r = 0,
    g = 0,
    b = 0;
  for (const p of box.pixels) {
    r += p[0];
    g += p[1];
    b += p[2];
  }
  const n = box.pixels.length || 1;
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
}

function sampleOpaquePixels(data: Uint8ClampedArray, max: number): RGB[] {
  const total = data.length / 4;
  const step = Math.max(1, Math.floor(total / max));
  const out: RGB[] = [];
  for (let i = 0; i < total; i += step) {
    const o = i * 4;
    if (data[o + 3] < 8) continue; // skip transparent
    out.push([data[o], data[o + 1], data[o + 2]]);
  }
  return out;
}

function dedupe(px: RGB[]): RGB[] {
  const seen = new Set<number>();
  const out: RGB[] = [];
  for (const [r, g, b] of px) {
    const key = (r << 16) | (g << 8) | b;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([r, g, b]);
  }
  return out;
}

// ── Nearest colour ──────────────────────────────────────────────────────

/** Index of the palette colour closest (squared Euclidean) to (r,g,b). */
export function nearestIndex(r: number, g: number, b: number, palette: RGB[]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const p = palette[i];
    const dr = r - p[0];
    const dg = g - p[1];
    const db = b - p[2];
    const d = dr * dr + dg * dg + db * db;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

// ── Brightness / contrast ───────────────────────────────────────────────

/**
 * Apply brightness and contrast in place. `brightness` and `contrast` are
 * -100..100. Contrast uses the classic pivot-around-128 factor.
 */
export function applyBrightnessContrast(
  data: Uint8ClampedArray,
  brightness: number,
  contrast: number,
): void {
  const bright = clamp(brightness, -100, 100) * 1.28; // → roughly -128..128
  const c = clamp(contrast, -100, 100) * 2.55; // → -255..255
  const factor = (259 * (c + 255)) / (255 * (259 - c));
  if (bright === 0 && c === 0) return;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp255(factor * (data[i] + bright - 128) + 128);
    data[i + 1] = clamp255(factor * (data[i + 1] + bright - 128) + 128);
    data[i + 2] = clamp255(factor * (data[i + 2] + bright - 128) + 128);
  }
}

// ── Error-diffusion kernels ─────────────────────────────────────────────

export interface DiffusionPoint {
  dx: number;
  dy: number;
  w: number;
}
export interface Kernel {
  points: DiffusionPoint[];
  divisor: number;
}

export const FLOYD: Kernel = {
  divisor: 16,
  points: [
    { dx: 1, dy: 0, w: 7 },
    { dx: -1, dy: 1, w: 3 },
    { dx: 0, dy: 1, w: 5 },
    { dx: 1, dy: 1, w: 1 },
  ],
};

/** Atkinson diffuses only 6/8 of the error — the crisp early-Mac look. */
export const ATKINSON: Kernel = {
  divisor: 8,
  points: [
    { dx: 1, dy: 0, w: 1 },
    { dx: 2, dy: 0, w: 1 },
    { dx: -1, dy: 1, w: 1 },
    { dx: 0, dy: 1, w: 1 },
    { dx: 1, dy: 1, w: 1 },
    { dx: 0, dy: 2, w: 1 },
  ],
};

export const JJN: Kernel = {
  divisor: 48,
  points: [
    { dx: 1, dy: 0, w: 7 },
    { dx: 2, dy: 0, w: 5 },
    { dx: -2, dy: 1, w: 3 },
    { dx: -1, dy: 1, w: 5 },
    { dx: 0, dy: 1, w: 7 },
    { dx: 1, dy: 1, w: 5 },
    { dx: 2, dy: 1, w: 3 },
    { dx: -2, dy: 2, w: 1 },
    { dx: -1, dy: 2, w: 3 },
    { dx: 0, dy: 2, w: 5 },
    { dx: 1, dy: 2, w: 3 },
    { dx: 2, dy: 2, w: 1 },
  ],
};

export const STUCKI: Kernel = {
  divisor: 42,
  points: [
    { dx: 1, dy: 0, w: 8 },
    { dx: 2, dy: 0, w: 4 },
    { dx: -2, dy: 1, w: 2 },
    { dx: -1, dy: 1, w: 4 },
    { dx: 0, dy: 1, w: 8 },
    { dx: 1, dy: 1, w: 4 },
    { dx: 2, dy: 1, w: 2 },
    { dx: -2, dy: 2, w: 1 },
    { dx: -1, dy: 2, w: 2 },
    { dx: 0, dy: 2, w: 4 },
    { dx: 1, dy: 2, w: 2 },
    { dx: 2, dy: 2, w: 1 },
  ],
};

export function kernelFor(algo: AlgoId): Kernel | null {
  switch (algo) {
    case 'floyd':
      return FLOYD;
    case 'atkinson':
      return ATKINSON;
    case 'jjn':
      return JJN;
    case 'stucki':
      return STUCKI;
    default:
      return null; // ordered / none are not error-diffusion
  }
}

// ── Bayer ordered matrices ──────────────────────────────────────────────

/** Build the 2^k × 2^k Bayer matrix, normalised to [0,1). */
export function bayerMatrix(k: number): { size: number; values: Float32Array } {
  let m = [[0]];
  for (let level = 0; level < k; level++) {
    const n = m.length;
    const next: number[][] = Array.from({ length: n * 2 }, () => new Array(n * 2).fill(0));
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const base = 4 * m[y][x];
        next[y][x] = base + 0;
        next[y][x + n] = base + 2;
        next[y + n][x] = base + 3;
        next[y + n][x + n] = base + 1;
      }
    }
    m = next;
  }
  const size = m.length;
  const values = new Float32Array(size * size);
  const denom = size * size;
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) values[y * size + x] = (m[y][x] + 0.5) / denom;
  return { size, values };
}

export const BAYER4 = bayerMatrix(2); // 4×4
export const BAYER8 = bayerMatrix(3); // 8×8

/**
 * Estimate a good ordered-dither perturbation amplitude for an arbitrary
 * palette: the average distance from each colour to its nearest neighbour,
 * converted to a per-channel figure. Auto-adapts to mono vs 32-colour.
 */
export function paletteSpread(palette: RGB[]): number {
  if (palette.length < 2) return 0;
  let sum = 0;
  for (let i = 0; i < palette.length; i++) {
    let nearest = Infinity;
    for (let j = 0; j < palette.length; j++) {
      if (i === j) continue;
      const dr = palette[i][0] - palette[j][0];
      const dg = palette[i][1] - palette[j][1];
      const db = palette[i][2] - palette[j][2];
      const d = Math.sqrt(dr * dr + dg * dg + db * db);
      if (d < nearest) nearest = d;
    }
    sum += nearest;
  }
  const avg = sum / palette.length;
  return clamp((avg / Math.sqrt(3)) * 0.9, 4, 255);
}

// ── The dithering entry points ──────────────────────────────────────────

export interface DitherOutput {
  out: Uint8ClampedArray; // RGBA
  colorsUsed: number;
}

/** Dither `img` to `palette` using the chosen algorithm. Returns RGBA output. */
export function ditherImage(
  img: WorkImage,
  palette: RGB[],
  algo: AlgoId,
  serpentine: boolean,
): DitherOutput {
  const kernel = kernelFor(algo);
  if (kernel) return errorDiffusion(img, palette, kernel, serpentine);
  if (algo === 'bayer4') return ordered(img, palette, BAYER4);
  if (algo === 'bayer8') return ordered(img, palette, BAYER8);
  return threshold(img, palette);
}

function threshold(img: WorkImage, palette: RGB[]): DitherOutput {
  const { data, width, height } = img;
  const out = new Uint8ClampedArray(width * height * 4);
  const used = new Set<number>();
  for (let i = 0; i < width * height; i++) {
    const o = i * 4;
    const idx = nearestIndex(data[o], data[o + 1], data[o + 2], palette);
    used.add(idx);
    writePixel(out, o, palette[idx], data[o + 3]);
  }
  return { out, colorsUsed: used.size };
}

function ordered(
  img: WorkImage,
  palette: RGB[],
  matrix: { size: number; values: Float32Array },
): DitherOutput {
  const { data, width, height } = img;
  const out = new Uint8ClampedArray(width * height * 4);
  const used = new Set<number>();
  const spread = paletteSpread(palette);
  const { size, values } = matrix;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      const t = (values[(y % size) * size + (x % size)] - 0.5) * spread;
      const idx = nearestIndex(data[o] + t, data[o + 1] + t, data[o + 2] + t, palette);
      used.add(idx);
      writePixel(out, o, palette[idx], data[o + 3]);
    }
  }
  return { out, colorsUsed: used.size };
}

function errorDiffusion(
  img: WorkImage,
  palette: RGB[],
  kernel: Kernel,
  serpentine: boolean,
): DitherOutput {
  const { data, width, height } = img;
  const out = new Uint8ClampedArray(width * height * 4);
  const used = new Set<number>();

  // Float working buffers so error accumulates without clamping mid-flight.
  const n = width * height;
  const fr = new Float32Array(n);
  const fg = new Float32Array(n);
  const fb = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    fr[i] = data[o];
    fg[i] = data[o + 1];
    fb[i] = data[o + 2];
  }

  const div = kernel.divisor;
  for (let y = 0; y < height; y++) {
    const ltr = !serpentine || y % 2 === 0;
    const xStart = ltr ? 0 : width - 1;
    const xEnd = ltr ? width : -1;
    const xStep = ltr ? 1 : -1;
    for (let x = xStart; x !== xEnd; x += xStep) {
      const i = y * width + x;
      const oldR = fr[i];
      const oldG = fg[i];
      const oldB = fb[i];
      const idx = nearestIndex(oldR, oldG, oldB, palette);
      used.add(idx);
      const pc = palette[idx];
      const errR = oldR - pc[0];
      const errG = oldG - pc[1];
      const errB = oldB - pc[2];
      const o = i * 4;
      writePixel(out, o, pc, data[o + 3]);

      for (const pt of kernel.points) {
        // Mirror the horizontal offset when scanning right-to-left.
        const dx = ltr ? pt.dx : -pt.dx;
        const nx = x + dx;
        const ny = y + pt.dy;
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const ni = ny * width + nx;
        const wgt = pt.w / div;
        fr[ni] += errR * wgt;
        fg[ni] += errG * wgt;
        fb[ni] += errB * wgt;
      }
    }
  }
  return { out, colorsUsed: used.size };
}

function writePixel(out: Uint8ClampedArray, o: number, color: RGB, alpha: number): void {
  out[o] = color[0];
  out[o + 1] = color[1];
  out[o + 2] = color[2];
  out[o + 3] = alpha; // preserve source alpha
}

// ── Small numeric helpers ───────────────────────────────────────────────

export function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}
export function clampInt(n: number, lo: number, hi: number): number {
  return Math.round(clamp(n, lo, hi));
}
function clamp255(n: number): number {
  return n < 0 ? 0 : n > 255 ? 255 : n;
}
